<script setup lang="ts">
import { computed } from 'vue';
import { statusTone, statusWord } from '@/components/DataTable/statusChips';
import { filterControlMappings } from '@/components/FilterBuilder/filterControlRegistry';
import { idLookupDisplayString } from '@/constants/lookupFilterOptions';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * A status: a coloured dot and the word, such as "● Active". The colour comes
 * from the same rule as the status chips in tables, and the word always shows,
 * so colour is never the only signal.
 */
const props = defineProps<{
    entityId: string;
    column: TableColumnStaticProps;
    value: unknown;
}>();

const DOT_CLASS = {
    good: 'bg-emerald-500',
    bad: 'bg-red-500',
    pending: 'bg-amber-400',
    neutral: 'bg-muted-foreground/60',
} as const;

const word = computed(() => {
    const optionsSource = filterControlMappings[props.entityId]?.[props.column.key]?.optionsSource;
    const lookupWord = idLookupDisplayString(optionsSource, props.value);
    if (lookupWord !== null) {
        return lookupWord;
    }
    return statusWord(props.column.id, props.value) ?? '';
});
</script>

<template>
  <span class="inline-flex max-w-full items-center gap-2" data-testid="status-value">
    <span class="size-2 shrink-0 rounded-full" :class="DOT_CLASS[statusTone(word)]" aria-hidden="true" data-testid="status-dot" />
    <span class="min-w-0">{{ word }}</span>
  </span>
</template>
